import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import "./TreeViewZoomControls.scss";
import { Button } from "../../../common/components/Button/Button";
import { IconInfoCircle } from "../../../common/icons/IconInfoCircle/IconInfoCircle";
import { IconMinus } from "../../../common/icons/IconMinus/IconMinus";
import { IconPlus } from "../../../common/icons/IconPlus/IconPlus";
import { IconReset } from "../../../common/icons/IconReset/IconReset";
import { IconTreeViewFullScreen } from "../../../common/icons/IconTreeViewFullScreen/IconTreeViewFullScreen";
import { IconExpand } from "../../../common/icons/IconExpand/IconExpand";
import { IconCollapse } from "../../../common/icons/IconCollapse/IconCollapse";
import { IconMinimize } from "../../../common/icons/IconMinimize/IconMinimize";
import defaultContent from "../../../content/treeView";
import { TreeViewInfoKeyModal } from "../TreeViewInfoKeyModal/TreeViewInfoKeyModal";

interface TreeViewZoomControlsProps {
  zoomPercent?: number;
  /** Overrides the default "open the info key modal" behavior of the info button - used by
   *  Storybook to show an alert instead of rendering the real modal. */
  onInfoClick?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onReset?: () => void;
  onFullscreenClick?: () => void;
  isFullscreen?: boolean;
  /** True once the tree shows more than root + first-level downlines - switches the
   *  button from Expand All to Collapse All. */
  isExpandedBeyondFrontline?: boolean;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
}

/**
 * Zoom controls and actions toolbar for LOS within Account Management Workspace.
 */
export const TreeViewZoomControls = ({
  zoomPercent = 60,
  onInfoClick,
  onZoomIn,
  onZoomOut,
  onReset,
  onFullscreenClick,
  isFullscreen = false,
  isExpandedBeyondFrontline = false,
  onExpandAll,
  onCollapseAll,
}: TreeViewZoomControlsProps) => {
  const { t } = useTranslation(["treeView"]);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const handleInfoClick = useCallback(() => {
    if (onInfoClick) {
      onInfoClick();
      return;
    }
    setIsInfoModalOpen(true);
  }, [onInfoClick]);

  return (
    <div className="tree-view-zoom-controls">
      <Button
        type="button"
        className="tree-view-zoom-controls__icon-button"
        onClick={handleInfoClick}
        aria-label={String(t("accessibilityLabels.infoKeyButton", defaultContent.accessibilityLabels.infoKeyButton))}
        title={String(t("infoKey.title", defaultContent.infoKey.title))}
      >
        <IconInfoCircle />
      </Button>

      <div className="tree-view-zoom-controls__divider" />

      <div className="tree-view-zoom-controls__zoom">
        <Button
          type="button"
          className="tree-view-zoom-controls__icon-button"
          onClick={onZoomOut}
          aria-label={String(t("accessibilityLabels.zoomOutButton", defaultContent.accessibilityLabels.zoomOutButton))}
        >
          <IconMinus />
        </Button>
        <span className="tree-view-zoom-controls__percent">{`${zoomPercent}%`}</span>
        <Button
          type="button"
          className="tree-view-zoom-controls__icon-button"
          onClick={onZoomIn}
          aria-label={String(t("accessibilityLabels.zoomInButton", defaultContent.accessibilityLabels.zoomInButton))}
        >
          <IconPlus />
        </Button>
      </div>

      <div className="tree-view-zoom-controls__divider" />

      <Button
        type="button"
        className="tree-view-zoom-controls__action"
        onClick={onReset}
        aria-label={String(t("accessibilityLabels.resetButton", defaultContent.accessibilityLabels.resetButton))}
      >
        <IconReset />
        <span className="tree-view-zoom-controls__action-label">{t("reset", defaultContent.reset)}</span>
      </Button>

      <div className="tree-view-zoom-controls__divider" />

      <Button
        type="button"
        className="tree-view-zoom-controls__action"
        onClick={isExpandedBeyondFrontline ? onCollapseAll : onExpandAll}
        aria-label={String(
          isExpandedBeyondFrontline
            ? t("accessibilityLabels.collapseAllButton", defaultContent.accessibilityLabels.collapseAllButton)
            : t("accessibilityLabels.expandAllButton", defaultContent.accessibilityLabels.expandAllButton),
        )}
      >
        {isExpandedBeyondFrontline ? <IconCollapse /> : <IconExpand />}
        <span className="tree-view-zoom-controls__action-label">
          {isExpandedBeyondFrontline
            ? t("collapseAll", defaultContent.collapseAll)
            : t("expandAll", defaultContent.expandAll)}
        </span>
      </Button>

      <div className="tree-view-zoom-controls__divider" />

      <Button
        type="button"
        className="tree-view-zoom-controls__action"
        onClick={onFullscreenClick}
        aria-label={String(
          t("accessibilityLabels.fullscreenButton", defaultContent.accessibilityLabels.fullscreenButton),
        )}
      >
        {isFullscreen ? <IconMinimize /> : <IconTreeViewFullScreen />}
        <span className="tree-view-zoom-controls__action-label">
          {isFullscreen
            ? t("exitFullscreen", defaultContent.exitFullscreen)
            : t("fullscreen", defaultContent.fullscreen)}
        </span>
      </Button>

      {!onInfoClick && <TreeViewInfoKeyModal open={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} />}
    </div>
  );
};
